import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AlertCircle, AlertTriangle, Info, ChevronRight, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { getAuthHeaders } from '@/contexts/AuthContext'

// DEMO MODE: Use mock data (set to false once API authentication is configured)
const USE_MOCK_DATA = false;

type AlertSeverity = 'critical' | 'warning' | 'info'

interface Alert {
  id: string
  severity: AlertSeverity
  title: string
  vehicle_id: string
  vehicle_name: string
  timestamp: number
  description?: string
}

// Mock data for demo
const mockAlerts: { alerts: Alert[] } = {
  alerts: [
    {
      id: '1',
      severity: 'critical',
      title: 'Engine temperature high',
      vehicle_id: 'vehicle_142',
      vehicle_name: 'Vehicle #142',
      timestamp: Date.now() - 2 * 60 * 1000, // 2 minutes ago
      description: 'Engine coolant temperature exceeds safe threshold'
    },
    {
      id: '2',
      severity: 'warning',
      title: 'Approaching HOS limit',
      vehicle_id: 'vehicle_023',
      vehicle_name: 'Driver #23',
      timestamp: Date.now() - 15 * 60 * 1000, // 15 minutes ago
      description: 'Driver approaching hours of service limit'
    },
    {
      id: '3',
      severity: 'info',
      title: 'Maintenance due soon',
      vehicle_id: 'vehicle_089',
      vehicle_name: 'Vehicle #089',
      timestamp: Date.now() - 60 * 60 * 1000, // 1 hour ago
      description: 'Scheduled maintenance due in 3 days'
    }
  ]
};

const severityConfig = {
  critical: {
    icon: AlertCircle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    badge: 'destructive' as const
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    badge: 'secondary' as const
  },
  info: {
    icon: Info,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    badge: 'secondary' as const
  }
}

export function RealTimeAlerts() {
  const { data: alerts, isLoading } = useQuery({
    queryKey: ['real-time-alerts'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockAlerts;
      const res = await fetch('/api/fleet/alerts?limit=5', {
        headers: getAuthHeaders()
      })
      if (!res.ok) throw new Error('Failed to fetch alerts')
      return res.json() as Promise<{ alerts: Alert[] }>
    },
    refetchInterval: USE_MOCK_DATA ? false : 10000 // Refresh every 10 seconds
  })

  if (isLoading) {
    return (
      <Card className="w-80">
        <CardHeader>
          <CardTitle className="text-base font-medium">Real-Time Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const alertsList = alerts?.alerts || []

  return (
    <Card className="w-80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center justify-between">
          <span>Real-Time Alerts</span>
          {alertsList.length > 0 && (
            <Badge variant="destructive" className="ml-2">
              {alertsList.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alertsList.length === 0 ? (
          <div className="py-8 text-center">
            <Info className="h-8 w-8 mx-auto text-muted-foreground opacity-50 mb-2" />
            <p className="text-sm text-muted-foreground">No active alerts</p>
            <p className="text-xs text-muted-foreground mt-1">
              Your fleet is running smoothly
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {alertsList.map((alert) => {
              const config = severityConfig[alert.severity]
              const Icon = config.icon

              return (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${config.bg} hover:bg-accent/50 cursor-pointer transition-colors`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`h-4 w-4 mt-0.5 ${config.color}`} />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <Badge variant={config.badge} className="text-xs mb-1">
                            {alert.severity.toUpperCase()}
                          </Badge>
                          <p className="text-sm font-medium">{alert.title}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {alert.vehicle_name} • {formatDistanceToNow(alert.timestamp, { addSuffix: true })}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              )
            })}

            {alertsList.length >= 5 && (
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  // Navigate to alerts page
                  window.location.href = '/safety?tab=alerts'
                }}
              >
                View All Alerts
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
